import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  corpoRegistroExterno, erroArquivoComprovante, erroDaApi, erroValorParcial, formatarCentavos, reaisParaCentavos, type AmbienteRepasse,
} from "@/lib/repasse-externo";

export interface AlvoRepasseExterno {
  vistoriadoraId: string;
  vistoriadoraNome: string;
  ambiente: AmbienteRepasse;
  pendenteCents: number;
}

/** Tudo que mostra o "A pagar" desta Vistoriadora: resumo, extrato e histórico. */
export function invalidarRepasses(alvo: { vistoriadoraId: string; ambiente: string }) {
  queryClient.invalidateQueries({ queryKey: ["/api/repasses/resumo"] });
  queryClient.invalidateQueries({ queryKey: ["/api/repasses/ledger"] });
  queryClient.invalidateQueries({ queryKey: ["/api/owner/repasses/externo", alvo.vistoriadoraId, alvo.ambiente] });
}

export function RegistrarRepasseExternoDialog({ alvo, onClose }: { alvo: AlvoRepasseExterno | null; onClose: () => void }) {
  const { toast } = useToast();
  const [modalidade, setModalidade] = useState<"integral" | "parcial">("integral");
  const [valorTexto, setValorTexto] = useState("");
  const [observacao, setObservacao] = useState("");
  const [chave, setChave] = useState("");
  const [erroServidor, setErroServidor] = useState<string | null>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [comprovanteId, setComprovanteId] = useState<string | null>(null);

  // Cada abertura é um pagamento novo: campos limpos e chave de idempotência nova.
  // Reenviar no mesmo modal (queda de rede, clique duplo) reaproveita a chave e nunca debita duas vezes.
  useEffect(() => {
    if (!alvo) return;
    setModalidade("integral");
    setValorTexto("");
    setObservacao("");
    setErroServidor(null);
    setArquivo(null);
    setComprovanteId(null);
    setChave(crypto.randomUUID());
  }, [alvo]);

  const disponivel = alvo?.pendenteCents ?? 0;
  const valorCents = modalidade === "integral" ? disponivel : reaisParaCentavos(valorTexto);
  const erroParcial = modalidade === "parcial" ? erroValorParcial(valorCents, disponivel) : null;
  const erroArquivo = erroArquivoComprovante(arquivo);

  const registrar = useMutation({
    mutationFn: async () => {
      if (!alvo) throw new Error("Nenhuma Vistoriadora selecionada.");
      if (!arquivo || erroArquivo) throw new Error(erroArquivo ?? "Anexe o comprovante.");
      let anexoId = comprovanteId;
      if (!anexoId) {
        const consulta = new URLSearchParams({ vistoriadoraId: alvo.vistoriadoraId, ambiente: alvo.ambiente, chaveIdempotencia: chave, nome: arquivo.name });
        const anexo: { id: string } = await (await apiRequest("POST", `/api/owner/repasses/externo/comprovantes?${consulta}`, arquivo)).json();
        anexoId = anexo.id;
        setComprovanteId(anexoId);
      }
      const corpo = corpoRegistroExterno({
        vistoriadoraId: alvo.vistoriadoraId, ambiente: alvo.ambiente, modalidade, valorCents, observacao, chaveIdempotencia: chave, comprovanteId: anexoId,
      });
      return (await apiRequest("POST", "/api/owner/repasses/externo", corpo)).json() as Promise<{ registro: { valorCents: number } }>;
    },
    onSuccess: (r) => {
      if (alvo) invalidarRepasses(alvo);
      toast({ title: `Repasse registrado: ${formatarCentavos(r.registro.valorCents)}` });
      onClose();
    },
    onError: (e) => {
      const { message, code } = erroDaApi(e);
      setErroServidor(code === "REPASSE_VIA_ASAAS" ? 'Esta Vistoriadora agora tem subconta pronta. Use "Transferir agora".' : message);
      if (alvo) invalidarRepasses(alvo);
    },
  });

  const podeEnviar = !!alvo && disponivel > 0 && !erroParcial && !erroArquivo && !registrar.isPending;

  return (
    <Dialog open={!!alvo} onOpenChange={(open) => { if (!open && !registrar.isPending) onClose(); }}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar repasse fora da plataforma</DialogTitle>
          <DialogDescription>
            {alvo?.vistoriadoraNome} · {alvo?.ambiente}. Registre o valor que você já pagou por fora (PIX, TED etc.).
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (podeEnviar) registrar.mutate(); }}>
          <p className="text-sm">
            Disponível para registrar: <span className="font-semibold tabular-nums">{formatarCentavos(disponivel)}</span>
          </p>
          <RadioGroup value={modalidade} onValueChange={(v) => setModalidade(v as "integral" | "parcial")} disabled={registrar.isPending} className="space-y-2">
            <div className="flex items-center gap-2">
              <RadioGroupItem value="integral" id="repasse-integral" />
              <Label htmlFor="repasse-integral">Valor integral ({formatarCentavos(disponivel)})</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="parcial" id="repasse-parcial" />
              <Label htmlFor="repasse-parcial">Valor parcial</Label>
            </div>
          </RadioGroup>
          {modalidade === "parcial" && (
            <div className="space-y-1">
              <Label htmlFor="repasse-valor">Valor pago</Label>
              <Input
                id="repasse-valor" data-testid="repasse-externo-valor" type="number" inputMode="decimal" step="0.01" min="0.01"
                value={valorTexto} onChange={(e) => setValorTexto(e.target.value)}
                disabled={registrar.isPending}
                aria-invalid={!!erroParcial && valorTexto !== ""} aria-describedby="repasse-valor-erro" autoFocus
              />
              {erroParcial && valorTexto !== "" && <p id="repasse-valor-erro" className="text-sm text-red-700">{erroParcial}</p>}
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="repasse-observacao">Observação (opcional)</Label>
            <Textarea
              id="repasse-observacao" maxLength={500} value={observacao} onChange={(e) => setObservacao(e.target.value)}
              placeholder="Ex.: PIX enviado em 03/10, ID E123..."
              disabled={registrar.isPending}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="repasse-comprovante">Comprovante (obrigatório)</Label>
            <Input
              key={chave} id="repasse-comprovante" type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              disabled={registrar.isPending || !!comprovanteId} aria-describedby="repasse-comprovante-ajuda repasse-comprovante-erro"
              aria-invalid={!!arquivo && !!erroArquivo} required
              onChange={(event) => { setArquivo(event.target.files?.[0] ?? null); setErroServidor(null); }}
            />
            <p id="repasse-comprovante-ajuda" className="text-sm text-muted-foreground">
              PDF, JPG ou PNG de até 5 MB. O Admin da Vistoriadora poderá visualizar e baixar este arquivo.
            </p>
            {arquivo && !erroArquivo && <p className="break-words text-sm">{arquivo.name} · {(arquivo.size / 1024).toFixed(0)} KB{comprovanteId ? " · Enviado" : ""}</p>}
            <p id="repasse-comprovante-erro" role={arquivo && erroArquivo ? "alert" : undefined} className="text-sm text-destructive">{arquivo && erroArquivo ? erroArquivo : null}</p>
          </div>
          {valorCents !== null && !erroParcial && disponivel > 0 && (
            <p className="text-sm text-slate-600">
              Será registrado: <span className="font-semibold tabular-nums">{formatarCentavos(valorCents)}</span>. O valor sai do
              "A pagar" e fica no histórico. Dá para desfazer.
            </p>
          )}
          {erroServidor && <p role="alert" className="text-sm text-red-700">{erroServidor}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={registrar.isPending}>Cancelar</Button>
            <Button type="submit" disabled={!podeEnviar} data-testid="repasse-externo-confirmar">
              {registrar.isPending && <RefreshCw className="mr-2 h-4 w-4 animate-spin" />}{registrar.isPending ? "Confirmando repasse…" : "Registrar repasse"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
